/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import isEmail from 'validator/lib/isEmail';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';
import user from 'js/lib/user';

import { InlineNotification, TextField, Typography2 } from '@coursera/cds-core';
import { Button, useTracker } from '@coursera/event-pulse/react';

import type { RecoveryEmailData } from 'bundles/account-settings/types/RecoveryEmail';
import {
  insertRecoveryEmail as insertRecoveryEmailREST,
  sendVerificationEmail as sendRecoveryVerificationEmailREST,
} from 'bundles/account-settings/utils/RecoveryEmailAPIUtils';

import _t from 'i18n!nls/account-settings';

type Props = {
  ssoOrgName: string | undefined;
  ssoOrgSlug: string | undefined;
  recoveryEmailData?: RecoveryEmailData | null;
  onInsertSuccess?: (data: RecoveryEmailData | null) => void;
};

const verificationEmailSentStatus = {
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
} as const;

const styles = {
  // Offsets for the sticky Coursera navbar so it doesn't cover this section when scrolled to
  root: css`
    scroll-margin-top: var(--cds-spacing-1000);
  `,
  subtitle: css`
    margin-block-start: var(--cds-spacing-100);
    margin-block-end: var(--cds-spacing-200);
  `,
  notificationSpacing: css`
    margin-block-end: var(--cds-spacing-200);
  `,
  input: css`
    width: 100%;
  `,
  buttons: css`
    display: flex;
    gap: var(--cds-spacing-200);
    margin-top: var(--cds-spacing-150);
  `,
};

const inputName = 'recovery_email_settings_input';

const RecoveryEmail = ({ ssoOrgName, ssoOrgSlug, recoveryEmailData, onInsertSuccess }: Props) => {
  const secondaryEmail = recoveryEmailData
    ? { emailAddress: recoveryEmailData.emailAddress, isVerified: recoveryEmailData.isVerified }
    : undefined;

  const [isEditable, setIsEditable] = React.useState<boolean>(true);
  const [sendVerificationEmailLoading, setSendVerificationEmailLoading] = React.useState<boolean>(false);
  const [emailFieldError, setEmailFieldError] = React.useState<string>('');
  const [verificationEmailStatus, setVerificationEmailStatus] = React.useState<
    null | keyof typeof verificationEmailSentStatus
  >(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [emailField, setEmailField] = React.useState('');
  const secondaryEmailAddress = secondaryEmail?.emailAddress ?? '';
  const hasSecondaryEmailAddress = !!secondaryEmailAddress;
  const isSecondaryEmailVerified = hasSecondaryEmailAddress && !!secondaryEmail?.isVerified;
  const showUnverifiedEmailBanner =
    secondaryEmail &&
    !isEditable &&
    !isLoading &&
    !secondaryEmail?.isVerified &&
    verificationEmailStatus !== verificationEmailSentStatus.SUCCESS;
  const enterpriseProps = ssoOrgSlug ? { enterprise: { organizationSlug: ssoOrgSlug } } : {};
  const userId = user.get().id;
  const userEmail = user.get().email_address;
  const track = useTracker();

  React.useEffect(() => {
    if (secondaryEmailAddress) {
      setEmailField(secondaryEmailAddress);
      setIsEditable(false);
    }
  }, [secondaryEmailAddress]);

  const getValidationErrors = (): string => {
    if (!emailField) return _t('Please enter a recovery email address');
    if (emailField === userEmail) return _t('You cannot use your primary email as a recovery email');
    if (!isEmail(emailField)) return _t('Please enter a valid email address');
    return '';
  };

  const sendVerificationEmailUtil = (recoveryEmail: string) => {
    setSendVerificationEmailLoading(true);
    setVerificationEmailStatus(null);
    sendRecoveryVerificationEmailREST({ emailAddress: recoveryEmail, userId })
      .then(() => setVerificationEmailStatus(verificationEmailSentStatus.SUCCESS))
      .catch(() => setVerificationEmailStatus(verificationEmailSentStatus.ERROR))
      .finally(() => {
        setSendVerificationEmailLoading(false);
        setIsEditable(false);
        setIsLoading(false);
      });
  };

  const onSubmitEmail = async () => {
    const errors = getValidationErrors();
    if (errors) {
      track('validate_text_input', {
        textInput: { name: inputName },
        status: 'failure',
        errorCode: 'invalid_email',
        ...enterpriseProps,
      });
      setEmailFieldError(errors);
      return;
    }

    if (emailField === secondaryEmailAddress) {
      setIsEditable(false);
      return;
    }

    setIsLoading(true);

    insertRecoveryEmailREST({ emailAddress: emailField, userId })
      .then((res) => {
        if ('recoveryEmail' in res) {
          track('validate_text_input', {
            textInput: { name: inputName },
            status: 'success',
            ...enterpriseProps,
          });
          onInsertSuccess?.(res.recoveryEmail);
        }
        setEmailFieldError('');
        sendVerificationEmailUtil(emailField);
      })
      .catch(() => {
        setEmailFieldError(_t('There was an error while adding the email, please try again'));
        track('validate_text_input', {
          textInput: { name: inputName },
          status: 'failure',
          errorCode: 'network_error',
          ...enterpriseProps,
        });
        setIsEditable(false);
        setIsLoading(false);
      });
  };

  const onChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setEmailField(value);
    setEmailFieldError('');
  };

  const allowEditing = () => {
    setIsEditable(true);
  };

  const disableEditing = () => {
    setIsEditable(false);
    setEmailFieldError('');
    setEmailField(secondaryEmailAddress);
  };

  const showCancelButton = hasSecondaryEmailAddress && isEditable;
  const showSaveButton = !hasSecondaryEmailAddress || isEditable;
  const showEditButton = !isSecondaryEmailVerified && hasSecondaryEmailAddress && !isEditable;

  return (
    <div id="recovery-email-section" css={styles.root}>
      <Typography2 component="h2" variant="subtitleLarge">
        {_t('Recovery email')}
      </Typography2>
      <Typography2 component="p" variant="bodySecondary" color="supportText" css={styles.subtitle}>
        {ssoOrgName
          ? _t(
              'A personal recovery email ensures you can still access your accomplishments in case you lose access to your #{ssoOrgName} account.',
              { ssoOrgName }
            )
          : _t(
              'A personal recovery email ensures you can still access your accomplishments in case you lose access to your organization account.'
            )}
      </Typography2>
      {showUnverifiedEmailBanner && (
        <InlineNotification
          title={_t('Verify your recovery email ')}
          severity="warning"
          css={styles.notificationSpacing}
          inlineAction={
            <Button
              size="small"
              variant="ghost"
              onClick={() => sendVerificationEmailUtil(secondaryEmailAddress)}
              loading={sendVerificationEmailLoading}
              trackingData={{
                button: { name: 'settings_resent_recovery_email_button' },
                ...enterpriseProps,
              }}
            >
              {_t('Resend email')}
            </Button>
          }
        >
          <FormattedHTMLMessage
            message={
              ssoOrgName
                ? _t(
                    'Check your inbox for a verification email sent to <strong>#{secondaryEmailAddress}</strong>. Verify now to avoid losing access to your #{ssoOrgName} account in the future.',
                    { secondaryEmailAddress, ssoOrgName }
                  )
                : _t(
                    'Check your inbox for a verification email sent to <strong>#{secondaryEmailAddress}</strong>. Verify now to avoid losing access to your organization account in the future.',
                    { secondaryEmailAddress }
                  )
            }
          />
        </InlineNotification>
      )}
      <div role="status">
        {!isEditable && verificationEmailStatus === verificationEmailSentStatus.SUCCESS && (
          <InlineNotification severity="success" css={styles.notificationSpacing}>
            {_t('We sent you a new verification email')}
          </InlineNotification>
        )}
      </div>
      <div role="alert">
        {!isEditable && verificationEmailStatus === verificationEmailSentStatus.ERROR && (
          <InlineNotification severity="error" css={styles.notificationSpacing}>
            {_t('We couldn’t resend the verification email. Please try again')}
          </InlineNotification>
        )}
      </div>
      <TextField
        css={styles.input}
        value={emailField || ''}
        onChange={onChangeEmail}
        onBlur={getValidationErrors}
        aria-label={_t('Recovery email address')}
        validationStatus={emailFieldError ? 'error' : undefined}
        validationLabel={emailFieldError}
        placeholder={_t('Enter your personal email address (e.g., name@gmail.com)')}
        readOnly={!isEditable}
      />

      {(showSaveButton || showEditButton || showCancelButton) && (
        <div css={styles.buttons}>
          {showSaveButton && (
            <Button
              variant="primary"
              onClick={onSubmitEmail}
              loading={isLoading}
              trackingData={{
                button: { name: 'settings_recovery_email_button' },
                ...enterpriseProps,
              }}
            >
              {!hasSecondaryEmailAddress ? _t('Add Recovery') : _t('Save')}
            </Button>
          )}
          {showEditButton && (
            <Button
              variant="secondary"
              onClick={allowEditing}
              trackingData={{
                button: { name: 'settings_edit_recovery_email_button' },
                ...enterpriseProps,
              }}
            >
              {_t('Edit')}
            </Button>
          )}
          {showCancelButton && (
            <Button
              variant="ghost"
              onClick={disableEditing}
              trackingData={{
                button: { name: 'settings_cancel_recovery_email_button' },
                ...enterpriseProps,
              }}
            >
              {_t('Cancel')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default RecoveryEmail;
